import React, { useState } from 'react';
import { logError } from '../lib/logger';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RequestDemoModal = ({ open, onOpenChange }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    companyDescription: '',
    phone: '',
    message: '',
    product: 'UL101',
    fotricModel: '',
    mailingListOptIn: false
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact/submit`, {
        form_type: 'Request Demo',
        name: formData.name,
        email: formData.email,
        company: formData.company,
        company_description: formData.companyDescription,
        phone: formData.phone,
        message: formData.message,
        product: formData.product,
        fotric_model: formData.product === 'FOTRIC' ? formData.fotricModel : null,
        mailing_list_opt_in: formData.mailingListOptIn
      });

      if (response.data.success) {
        toast.success(response.data.message || "Thank you! We'll contact you shortly to schedule your demo.");
        
        setFormData({
          name: '',
          email: '',
          company: '',
          companyDescription: '',
          phone: '',
          message: '',
          product: 'UL101',
          fotricModel: '',
          mailingListOptIn: false
        });
        onOpenChange(false);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch (error) {
      logError('Form submission error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to submit form. Please try again or contact us directly at info@ctrlsys.com';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#1e2d3b]">Request a Demo</DialogTitle>
          <DialogDescription className="text-slate-600">
            Fill out the form below and our team will contact you to schedule a personalized demonstration of our ultrasonic aviation maintenance solutions.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Product Selection */}
          <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
            <Label className="text-base font-semibold text-[#1e2d3b]">Product Selection *</Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="product-ul101"
                  name="product"
                  value="UL101"
                  checked={formData.product === 'UL101'}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-blue-600"
                />
                <Label htmlFor="product-ul101" className="font-normal cursor-pointer">
                  UL101 Aviation Troubleshooter (B00033)
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="product-fotric"
                  name="product"
                  value="FOTRIC"
                  checked={formData.product === 'FOTRIC'}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-blue-600"
                />
                <Label htmlFor="product-fotric" className="font-normal cursor-pointer">
                  FOTRIC Acoustic Imaging Camera
                </Label>
              </div>

              {formData.product === 'FOTRIC' && (
                <div className="ml-6 mt-2">
                  <Label htmlFor="fotricModel" className="text-sm">Select Model *</Label>
                  <select
                    id="fotricModel"
                    name="fotricModel"
                    value={formData.fotricModel}
                    onChange={handleInputChange}
                    required={formData.product === 'FOTRIC'}
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a model...</option>
                    <option value="FOTRIC H4mini">FOTRIC H4mini</option>
                    <option value="FOTRIC H4">FOTRIC H4</option>
                    <option value="FOTRIC H6">FOTRIC H6</option>
                    <option value="FOTRIC H-Flex">FOTRIC H-Flex</option>
                    <option value="FOTRIC H7">FOTRIC H7</option>
                    <option value="FOTRIC P-MiX AcouTherm">FOTRIC P-MiX AcouTherm (2-in-1)</option>
                    <option value="FOTRIC V-MiX AcouTherm">FOTRIC V-MiX AcouTherm (2-in-1)</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="John Doe"
                className="border-slate-300 focus:border-blue-500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="john@company.com"
                className="border-slate-300 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="company">Company Name *</Label>
              <Input
                id="company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                required
                placeholder="Your Company"
                className="border-slate-300 focus:border-blue-500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+1 (555) 123-4567"
                className="border-slate-300 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="companyDescription">Company Type *</Label>
            <select
              id="companyDescription"
              name="companyDescription"
              value={formData.companyDescription}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select company type...</option>
              <option value="airline-operator">Airline/Operator</option>
              <option value="aircraft-mro">Aircraft MRO</option>
              <option value="aircraft-oem">Aircraft OEM</option>
              <option value="component-oem">Component OEM</option>
              <option value="component-repair">Component Repair/Overhaul Shop</option>
              <option value="fbo">FBO</option>
              <option value="line-maintenance">Line Maintenance Provider</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="What would you like to see in the demo? Any specific questions or requirements?"
              rows={4}
              className="border-slate-300 focus:border-blue-500 resize-none"
            />
          </div>

          <label className="flex items-start gap-3 rounded-lg bg-slate-50 p-3 border border-slate-200 cursor-pointer">
            <input
              type="checkbox"
              required
              data-testid="demo-optin-checkbox"
              checked={formData.mailingListOptIn}
              onChange={(e) => setFormData({ ...formData, mailingListOptIn: e.target.checked })}
              className="mt-1 h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
            />
            <span className="text-sm text-slate-700 leading-snug">
              <span className="text-red-600 font-semibold">*</span> Please add me to the ctrl.aero
              mailing list for product updates, case studies, and aviation maintenance insights.
              Required to submit.
            </span>
          </label>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 text-[15px]"
          >
            {isSubmitting ? (
              'Submitting...'
            ) : (
              <>
                <Send className="mr-2 h-5 w-5" />
                Request Demo
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RequestDemoModal;
